import { Divider, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckIcon, EmergencyIcon } from 'assets/svg';
import {
  VrsCallCommentsAbusePersonControl,
  VrsCallCommentsAbuseTextareaControl,
  VrsCallCommentsAbuseWitnessControl,
  VrsCallCommentsBasePopup,
  VrsCallCommentsFormButtons,
  VrsCallCommentsIncidentControl,
  VrsCallCommentsReasonControl,
} from 'features/call/vrs-call-comments/components';
import {
  useVrsCallCommentsAbuseForm,
  useVrsCallCommentsFormSubmit,
} from 'features/call/vrs-call-comments/hooks';
import type { VrsCallCommentsAbuseForm } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import type { BadgeProps } from 'features/ui/elements';
import { Badge } from 'features/ui/elements';

export const VrsCallCommentsAbuse = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'abuseCommentText',
  });
  const { handleSubmit, control, trigger } = useForm<VrsCallCommentsAbuseForm>({
    mode: 'onChange',
    defaultValues: {
      commentScenario: '',
      commentReason: '',
      incidentType: '',
      personType: '',
      witnessType: '',
    },
  });
  const { isValid, isDirty } = useFormState({ control });
  const { prepareFormData } = useVrsCallCommentsAbuseForm();
  const { submitCallComments } = useVrsCallCommentsFormSubmit();

  const validationStatusBadgeProps = useMemo<BadgeProps>(() => {
    if (isValid) {
      return {
        icon: CheckIcon,
        severity: 'success',
        children: t('complete'),
      };
    }

    return {
      icon: EmergencyIcon,
      severity: 'warning',
      children: t('incomplete'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const submitForm = async (formData: VrsCallCommentsAbuseForm) => {
    const callCommentsFormData = prepareFormData(formData);
    await submitCallComments(callCommentsFormData);
  };

  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VrsCallCommentsBasePopup
      icon={<EmergencyStyledIcon title='abuse-emergency-icon' />}
      title={t('title')}
      subtitle={t('subtitle')}
      containerTitle={t('containerTitle')}
      badge={<Badge {...validationStatusBadgeProps} />}
    >
      <StyledForm onSubmit={handleSubmit(submitForm)}>
        <ScrollableContainer>
          <VrsCallCommentsReasonControl control={control} />
          <VrsCallCommentsIncidentControl control={control} />
          <VrsCallCommentsAbusePersonControl control={control} />
          <VrsCallCommentsAbuseWitnessControl control={control} />
          {isDirty && <VrsCallCommentsAbuseTextareaControl control={control} />}
          <Separator title='abuse-divider' />
          <Typography
            variant='subtitle2'
            color={theme.palette.grey[300]}
            mt={12}
          >
            {t('description')}
          </Typography>
          <Typography
            variant='subtitle2'
            color={theme.palette.grey[300]}
            mt={12}
          >
            {t('endDescription')}
          </Typography>
        </ScrollableContainer>
        <VrsCallCommentsFormButtons isValid={isValid} />
      </StyledForm>
    </VrsCallCommentsBasePopup>
  );
};

const EmergencyStyledIcon = styled(EmergencyIcon)`
  height: 60px;
  width: 60px;
  path {
    stroke: ${theme.palette.grey[100]};
  }
`;

const Separator = styled(Divider)`
  border-color: ${theme.palette.grey[600]};
  margin: ${theme.spacing(8, 0)};
`;

const StyledForm = styled('form')`
  margin-top: ${theme.spacing(2)};
`;

const ScrollableContainer = styled('div')`
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;
